import { forwardRef, useEffect, useState } from 'react';
import { useApi } from '@/lib/legacy';
import { currencyFormatter as currencyFormatterJs } from '../QuotePage/content/QuoteGrids/utils';
import { ActionButton, ActionRow, Grid, type Row } from './tabParts';

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- boundary: QuoteGrids/utils.js formats an AG Grid value
const currencyFormatter = currencyFormatterJs as any as (args: { field: unknown; withDecimals?: boolean }) => string;

const columnDefs = [
  {
    headerName: 'Location Per Occurrence',
    field: 'falsePretensePerLocPerOcc',
    editable: true,
    valueFormatter: (field: unknown) => currencyFormatter({ field, withDecimals: true }),
  },
  {
    headerName: 'Limit of Insurance',
    field: 'falsePretensePolicyLimit',
    editable: true,
    valueFormatter: (field: unknown) => currencyFormatter({ field, withDecimals: true }),
  },
];

const deductibleFormatter = (field: { value: number }) => {
  const value = field.value;
  return value === 0 || isNaN(value) ? 'N/A' : currencyFormatter({ field });
};

const locationColumns = [
  { headerName: 'Location Address', field: 'address', editable: false },
  { headerName: 'Per Auto Deductible', field: 'autoDeductible', editable: false, valueFormatter: deductibleFormatter },
  { headerName: 'Per Loss Deductible', field: 'lossDeductible', editable: false, valueFormatter: deductibleFormatter },
];

/** The policy's false pretense limits, with each location's deductibles; save or download the form. */
export const FalsePretense = forwardRef<unknown, { policy: Row; setGridReference: (ref: unknown) => void }>(({ policy, setGridReference }) => {
  const { createEndorsement, downloadEndorsement } = useApi();
  const [rowData, setRowData] = useState<Row | undefined>();
  const [locationData, setLocationData] = useState<Row[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    setRowData(policy?.falsePretense);
  }, [policy]);

  useEffect(() => {
    const locations = policy?.locations;
    setLocationData(
      locations.map((location: Row) => ({
        address: location.address,
        autoDeductible: location.coverages.find((coverage: Row) => coverage.coverageType === 'falsePretense')?.deductible || null,
        lossDeductible: 0,
      }))
    );
  }, [policy]);

  const payload = { endorsementType: 'falsePretense', endorsementData: { falsePretense: rowData } };

  const handleOnSave = async () => {
    setIsLoading(true);
    try {
      const response = await createEndorsement(policy.id, payload);
      if (response) {
        window.open(response.value, '_blank');
      }
    } catch (error) {
      console.error('Error saving payload:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOnDownload = async () => {
    setIsDownloading(true);
    try {
      const response = await downloadEndorsement(policy.id, payload);
      if (response) {
        window.open(response.data.value, '_blank');
      }
    } catch (error) {
      console.error('Error saving payload:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div>
      <div className="mb-5 h-[60px] w-full">
        <Grid
          data={[rowData]}
          columnDefs={columnDefs}
          defaultColDef={{ flex: 1, resizable: true }}
          setGridReference={setGridReference}
          onCellValueChanged={(params: { colDef: { field: string }; data: Row }) => {
            setRowData({ ...rowData, [params.colDef.field]: params.data[params.colDef.field] });
          }}
          floatingFilter={false}
          shouldShowPagination={false}
        />
      </div>
      <div className="grid-container max-h-[600px] overflow-y-auto">
        <Grid
          data={locationData}
          columnDefs={locationColumns}
          defaultColDef={{ flex: 1, resizable: true }}
          setGridReference={setGridReference}
          floatingFilter={false}
          shouldShowPagination={false}
        />
      </div>
      <ActionRow>
        <ActionButton onClick={handleOnSave} loading={isLoading}>
          Save
        </ActionButton>
        <ActionButton onClick={handleOnDownload} loading={isDownloading}>
          Download
        </ActionButton>
      </ActionRow>
    </div>
  );
});
FalsePretense.displayName = 'FalsePretense';
